/** @jsxImportSource @emotion/react */
import {useDispatch, useSelector, useStore} from 'react-redux';
import {IAppState} from '../core/redux/IAppState';
import {Resort} from '../resortDetails/resort';
import FloatingMenu from '../layout/menu/FloatingMenu';
import solidArrow from '../assets/icons/downArrowSolid.svg';
import {css} from '@emotion/react';
import {MediumText, UNSELECTABLE_TEXT} from '../styles/styles';
import * as React from 'react';
import {selectedResortAction} from '../auth/login/selectedResortAction';
import {useCallback, useEffect, useRef} from 'react';
import {WHITE} from '../styles/colors';
import {getConfigAction} from '../auth/config/getConfigAction';
import {getResortMappingAction} from '../resortMappingTool/getResortMappingAction';
import {updateLastResortAction} from '../auth/updateLastResortAction';
import { isNilOrEmpty } from '../language/module';
import { DEFAULT_CONFIG } from '../auth/config/defaultConfig';
import { defaultConfigAction } from '../auth/config/defaultConfigAction';
import { getError, getLoading } from '../core/redux/api/module';
import { ACTION_TYPE } from '../core/redux/actionType';
import { showSnackbarAction } from '../snackbar/snackbarAction';
import { getResortMappingErrorAction, resortMappingErrorRequestedAction, shouldLoadResortMappingErrors } from '../resortMappingTool/getResortMappingErrorAction';
import { getResortMappingHasErrorAction } from '../resortMappingTool/getResortMappingHasErrorAction';
import { getResource } from '../language/resources/getResource';
import { clearResortMappingAction } from '../resortMappingTool/clearResortMappingAction';
import { getScriptVersionAction } from '../admin/systemAdmin/getScriptVersionAction';
import { shallowEqual } from 'react-redux';
import { TooltipFC } from '../layout/TooltipFC';

const floatingMenuArrow = css`
  width: 19px; /* Odd dimensions size image to the height of a lower-case letter @ 2rem */
  height: 9.5px;
  padding-left: 5px;
  padding-right: 5px;
  padding-top: 5px;
  ${UNSELECTABLE_TEXT};
`;

const resortName = css`  
  color: ${WHITE}; 
  opacity: .9;
  ${MediumText};
`;


export const ResortSelectMenu = () => {
  const dispatch = useDispatch();
  const [resorts, setResorts] = React.useState<Resort[]>([]);
  const userResorts = useSelector((state: IAppState) => state.userState.resortLocations, shallowEqual);
  const selectedResort = useSelector((state: IAppState) => state.selectedResort);
  const allResorts = useSelector((state: IAppState) => state.resortDetails?.allResorts.getValues(), shallowEqual);
  const user = useSelector((state: IAppState) => state.userState);
  const resortConfig = useSelector((state: IAppState) => state.resortToolsConfig);
  const loadConfigError = useSelector((state: IAppState) => getError(state, ACTION_TYPE.APP_CONFIG_REQUEST, ACTION_TYPE.APP_CONFIG_REQUEST));
  const isLoadingConfig = useSelector((state: IAppState) => !!getLoading(state, ACTION_TYPE.APP_CONFIG_REQUEST, ACTION_TYPE.APP_CONFIG_REQUEST));
  const resources = useSelector((state: IAppState) => state.locale);
  // A switch clears the mapping rows, edits included
  const hasUnsavedEdits = useSelector((state: IAppState) => state.productMappingHasUnsavedEdits);
  // Marker read at dispatch time: as a selector it would rerun the effect that writes it
  const store = useStore<IAppState>();

  const showError = useCallback((errorMsg: string) => dispatch(showSnackbarAction(errorMsg, true)), [dispatch])
  const handleResortChange = useCallback((resortId: number) => {
    dispatch(clearResortMappingAction());
    dispatch(selectedResortAction(resortId));
    dispatch(getConfigAction(resortId));
    dispatch(updateLastResortAction(user, resortId))
  },[dispatch, user])

const hasInitializedResort = useRef(false);
const selectedResortIdRef = useRef(selectedResort.resortId);

useEffect(() => {
  selectedResortIdRef.current = selectedResort.resortId;
});

useEffect(() => {
  if (allResorts?.length && userResorts?.length && !hasInitializedResort.current) {
    hasInitializedResort.current = true;
    const filteredResorts = allResorts
      .filter((resort: Resort) => userResorts.includes(resort.resortId))
      .sort((a, b) => (a.resortName > b.resortName ? 1 : -1));

    setResorts(filteredResorts);

    if (!filteredResorts.some((r) => r.resortId === selectedResortIdRef.current)) {
      handleResortChange(filteredResorts[0].resortId);
    }
  }
}, [allResorts, userResorts, handleResortChange]);

  useEffect(() => {
    if (loadConfigError) {
      dispatch(defaultConfigAction(DEFAULT_CONFIG));
      showError(getResource(resources, 'RESORT_SELECT_MENU_LOAD_CONFIG_ERROR', selectedResort.resortName));
    }
  }, [dispatch, loadConfigError, resources, selectedResort.resortName, showError])

  // getConfigAction's bailout is keyed by action type, so a second switch while the first resort's config
  // is in flight is dropped and never re-asked, leaving every load that waits on the config waiting for
  // good. Ask again, once per resort, when a config load finishes still describing another one.
  const wasLoadingConfig = useRef(false);
  const configRetriedFor = useRef<number | undefined>(undefined);

  useEffect(() => {
    const configLoadSettled = wasLoadingConfig.current && !isLoadingConfig;
    wasLoadingConfig.current = isLoadingConfig;

    if (!configLoadSettled
      || parseInt(resortConfig.resortId) === selectedResort.resortId
      || configRetriedFor.current === selectedResort.resortId) return;

    configRetriedFor.current = selectedResort.resortId;
    dispatch(getConfigAction(selectedResort.resortId));
  }, [dispatch, isLoadingConfig, resortConfig.resortId, selectedResort.resortId])

  useEffect(() => {
    const resortId = parseInt(resortConfig.resortId);
    if (resortId === selectedResort.resortId) {
      const resortAbbrev = allResorts?.find((resort: Resort) => resort.resortId === resortId)?.resortAbbreviation;
      if (resortConfig.enableResortHydrationUIFeatures) {
        dispatch(getResortMappingHasErrorAction(resortAbbrev ?? ''));   
      }
      if (resortConfig.enableProductMapping.includes(user.role ?? 'agent') && resortConfig.enableResortHydrationUIFeatures) {
        dispatch(getResortMappingAction(false, resortAbbrev ?? '', true));
        if (shouldLoadResortMappingErrors(store.getState(), selectedResort.resortAbbreviation)) {
          dispatch(resortMappingErrorRequestedAction(selectedResort.resortAbbreviation));
          dispatch(getResortMappingErrorAction(selectedResort.resortAbbreviation));
        }
      }
      if (resortConfig.enableResortHydrationUIFeatures) {
        dispatch(getScriptVersionAction(selectedResort.resortAbbreviation));
      }
    }
  }, [allResorts, dispatch, resortConfig, selectedResort.resortAbbreviation, selectedResort.resortId, store, user.role]);
  
  const generateMenuItems = (userResorts: Resort[]) => {
    return userResorts.map((resort: Resort) => {
      return {
        key: resort.resortId,
        text: resort.resortName,
        onClick: () => { 
          handleResortChange(resort.resortId); 
        },
        selected: selectedResort.resortId === resort.resortId,
      }
    })
  }

const shortenResortName = (resortName: string) => resortName.length > 15 ? resortName.substring(0, 15) + '...' : resortName


  return (
    <div css={css`margin-top: 20px; flex-grow: 1`}>
      <span css={resortName}>
        {shortenResortName(selectedResort.resortName)}
      </span>
      { 
        !isNilOrEmpty(userResorts) && userResorts?.length && userResorts.length > 1 &&
        <TooltipFC
          disabled={!hasUnsavedEdits}
          content={<span>{getResource(resources, 'RESORT_SELECT_MENU_UNSAVED_EDITS')}</span>}
          children={
            <FloatingMenu
              menuCss={css`max-height:calc(100vh - 200px); overflow-y: auto;`}
              menuZIndex={200}
              menuItems={generateMenuItems(resorts)}
              disabled={hasUnsavedEdits}
              children={
                <img alt='arrow'
                    src={solidArrow}
                    css={floatingMenuArrow}/>
            } />
          } />
      }
    </div>

  )
}